/**
 * Autorización para el tratamiento de datos personales y el envío de mensajes (LOPDP), en la versión para imprimir y firmar en papel.
 * Los fines son los mismos que se aceptan en pantalla (FINES_DATOS): el papel y la pantalla dicen lo mismo.
 * El texto legal lo debe revisar un abogado antes de usarse con pacientes (ver CLAUDE.md).
 */
import { FINES_DATOS, DERECHOS } from "./historia.ts";

export const VERSION_AUTORIZACION_DATOS = "2026.1";

export interface DatosResponsable { nombre: string; razonSocial?: string; ruc?: string; correo?: string; telefono?: string }
export interface FinImpreso { clave: string; texto: string; detalle: string; obligatorio: boolean }
export interface AutorizacionImpresa {
  titulo: string; version: string;
  /** Quién firma: el titular, o el representante legal si el paciente es menor de edad. */
  firmante: "titular" | "representante";
  parrafos: string[]; fines: FinImpreso[]; derechos: string[]; cierre: string[];
}

const DETALLE_FINES: Record<string, string> = {
  atencion: "Registrar su identificación, contacto y datos de salud para atenderle, elaborar su historia clínica, planes de tratamiento, recetas y documentos. Es necesario para la atención; la historia clínica debe conservarse por obligación legal.",
  recordatorios: "Enviarle por WhatsApp y por correo electrónico recordatorios y confirmaciones de sus citas, avisos de su atención y mensajes de seguimiento.",
  promociones: "Enviarle por WhatsApp y por correo electrónico saludos de cumpleaños, promociones y novedades de la clínica.",
  fotos: "Tomar fotografías clínicas (por ejemplo antes y después del tratamiento) y guardarlas en su historia clínica.",
  imagenes_ia: "Enviar sus radiografías y fotografías clínicas, sin su nombre ni su identificación, a un servicio de inteligencia artificial que ayuda al profesional a revisarlas o a simular el resultado de un tratamiento. La imagen no se usa para entrenar modelos; el profesional siempre revisa y decide. Puede decir que no sin que cambie su atención.",
};

export function textoAutorizacionDatos(r: DatosResponsable, menor: boolean): AutorizacionImpresa {
  const quien = menor ? "el representante legal del paciente" : "el titular de los datos";
  const dequien = menor ? "del representante legal del paciente" : "del titular de los datos";
  const lista = Object.values(DERECHOS).map((d) => d.toLowerCase());
  const derechos = `${lista.slice(0, -1).join(", ")} y ${lista[lista.length - 1]}`;
  const identidad = [r.razonSocial && r.razonSocial !== r.nombre ? r.razonSocial : "", r.ruc ? `RUC ${r.ruc}` : ""].filter(Boolean).join(", ");
  const contacto = [r.correo, r.telefono].filter(Boolean).join(" · ");
  return {
    titulo: "Autorización para el tratamiento de datos personales y el envío de mensajes",
    version: VERSION_AUTORIZACION_DATOS,
    firmante: menor ? "representante" : "titular",
    parrafos: [
      `${r.nombre}${identidad ? ` (${identidad})` : ""} es el responsable del tratamiento de los datos personales del paciente${contacto ? `. Puede contactarnos en: ${contacto}` : ""}.`,
      `Los datos que se recogen son de identificación y contacto y, por tratarse de atención odontológica, datos de salud, que la Ley Orgánica de Protección de Datos Personales considera sensibles. Por eso se requiere la autorización expresa ${dequien}. Esta autorización es libre: puede elegir qué fines acepta marcando cada casilla.`,
      "Para enviar los mensajes se usan servicios de mensajería (WhatsApp) y de correo electrónico.",
    ],
    fines: FINES_DATOS.map(([clave, texto]) => ({ clave, texto, detalle: DETALLE_FINES[clave] ?? "", obligatorio: clave === "atencion" })),
    derechos: [
      `Puede ejercer los derechos de ${derechos} dirigiéndose a la clínica por escrito o en persona. La clínica responde en un plazo de 15 días hábiles.`,
      "Puede revocar esta autorización en cualquier momento, sin que ello afecte su atención ni la validez del tratamiento anterior. Al revocar, se detienen los recordatorios, las promociones y las fotografías; la historia clínica se conserva por obligación legal.",
    ],
    cierre: [
      `Declaro que soy ${quien}, que he leído y entendido este documento y que mis elecciones quedan marcadas arriba.`,
    ],
  };
}
